// File: frontend/src/components/admin/AdminEntries.tsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  FaSearch,
  FaFilter,
  FaTimes,
  FaEdit,
  FaTrash,
  FaTrashRestore,
  FaEye,
  FaChevronUp,
  FaChevronDown,
  FaStar,
  FaExclamationTriangle,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaPlus,
  FaCommentAlt,
} from "react-icons/fa";
import {
  AdminEntryDto,
  AdminEntryFilters,
  AdminEntryPageResponse,
} from "../../types/adminEntry.types";
import { EntryTargetTypeEnum } from "../../models/enums/EntryTargetTypeEnum";
import {
  getAdminEntries,
  getAdminDeletedEntries,
  getAdminEditedEntries,
  createAdminEntry,
  updateAdminEntry,
  deleteAdminEntry,
  restoreAdminEntry,
  hardDeleteAdminEntry,
} from "../../services/adminEntryService";
import { Pagination } from "../../utils/Pagination";
import { formatAdminDate, truncateText } from "../../utils/adminUtils";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { AdminSuccessBanner } from "../common/AdminBanner";
import { generateMatchUrlFromEntry } from "../../utils/slugGenerator";
import {
  ReasonModal,
  CreateEntryModal,
  ViewEntryModal,
  EditEntryModal,
  DeleteEntryModal,
  HardDeleteEntryModal,
} from "./AdminEntryModals";
import "../entries/EntryPostForm.css";
import { InputClearButton } from "../common/InputClearButton";
import "./AdminEntries.css";

interface AdminEntriesProps {
  isAdmin: boolean;
}

// Inline sort icon — mirrors AdminMatches getSortIcon pattern
const SortIcon: React.FC<{ field: string; sortBy: string; sortDir: string }> = ({
  field, sortBy, sortDir,
}) => {
  if (sortBy !== field) return <FaSort className="sort-icon" />;
  return sortDir === "asc"
    ? <FaSortUp className="sort-icon active" />
    : <FaSortDown className="sort-icon active" />;
};

// Draft key for the create modal (not tied to a specific entry)
const ADMIN_CREATE_DRAFT_KEY = "admin_create_entry_draft";
// Draft key for edit modal — scoped per entry id
const adminEditDraftKey = (id: number) => `admin_edit_entry_draft_${id}`;

export const AdminEntries: React.FC<AdminEntriesProps> = ({ isAdmin }) => {
  const { t } = useTranslation();

  // ── data ─────────────────────────────────────────────────────────────────────
  const [entries, setEntries]             = useState<AdminEntryDto[]>([]);
  const [loading, setLoading]             = useState(true);
  const [successMessage, setSuccessMessage, clearSuccess] = useAutoDismiss();

  // ── pagination ────────────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages,  setTotalPages]  = useState(0);
  const [totalItems,  setTotalItems]  = useState(0);

  // ── filters ───────────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState<AdminEntryFilters>({
    search: "", authorUsername: "", targetTitle: "", targetType: "",
    showDeleted: false, showEdited: false, sortBy: "createdAt", sortDir: "desc",
  });
  const [searchInput,  setSearchInput]  = useState("");
  const [showFilters,  setShowFilters]  = useState(true);

  // ── modal ─────────────────────────────────────────────────────────────────────
  const [modalType, setModalType] = useState<
    "create" | "view" | "edit" | "delete" | "hardDelete" | null
  >(null);
  const [selectedEntry, setSelectedEntry] = useState<AdminEntryDto | null>(null);
  const [modalError,    setModalError]    = useState<string | null>(null);

  // Reason modal — shows delete or edit reason text in a readable modal
  const [reasonModalText, setReasonModalText] = useState<string | null>(null);

  // Refs for toolbar cursor-based insertions in create and edit modals
  const createTextareaRef = useRef<HTMLTextAreaElement>(null);
  const editTextareaRef   = useRef<HTMLTextAreaElement>(null);

  // form fields
  const [createMatchId, setCreateMatchId] = useState("");
  const [createContent, setCreateContent] = useState("");
  const [editContent,   setEditContent]   = useState("");
  const [deleteReason,  setDeleteReason]  = useState("");
  const [editReason,    setEditReason]    = useState("");

  // Preview toggle for create and edit modals
  const [createPreview, setCreatePreview] = useState(false);
  const [editPreview,   setEditPreview]   = useState(false);

  // Whether a saved draft was restored on modal open
  const [createDraftRestored, setCreateDraftRestored] = useState(false);
  const [editDraftRestored,   setEditDraftRestored]   = useState(false);

  // ── fetch ─────────────────────────────────────────────────────────────────────
  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      // Choose the correct endpoint based on active view mode
      const fn = filters.showDeleted
        ? getAdminDeletedEntries
        : filters.showEdited
          ? getAdminEditedEntries
          : getAdminEntries;
      const res: AdminEntryPageResponse = await fn(currentPage, 20, filters);
      setEntries(res.content);
      setTotalPages(res.totalPages);
      setTotalItems(res.totalItems);
    } catch {
      // Error shown only inside modal; table shows empty state
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  // Persist create-modal draft to localStorage on every keystroke
  useEffect(() => {
    if (createContent.trim()) {
      localStorage.setItem(ADMIN_CREATE_DRAFT_KEY, createContent);
    } else {
      localStorage.removeItem(ADMIN_CREATE_DRAFT_KEY);
    }
  }, [createContent]);

  // Persist edit-modal draft to localStorage on every keystroke (scoped to entry id)
  useEffect(() => {
    if (!selectedEntry) return;
    if (editContent.trim()) {
      localStorage.setItem(adminEditDraftKey(selectedEntry.id), editContent);
    } else {
      localStorage.removeItem(adminEditDraftKey(selectedEntry.id));
    }
  }, [editContent, selectedEntry]);

  // ── filter helpers ────────────────────────────────────────────────────────────
  const applySearch  = () => { setFilters((p) => ({ ...p, search: searchInput })); setCurrentPage(0); };
  // Clear-X next to the search box: empties the field AND drops the applied search
  const clearSearch  = () => { setSearchInput(""); setFilters((p) => ({ ...p, search: "" })); setCurrentPage(0); };
  const clearFilters = () => {
    setSearchInput("");
    setFilters({ search: "", authorUsername: "", targetTitle: "", targetType: "", showDeleted: false, showEdited: false, sortBy: "createdAt", sortDir: "desc" });
    setCurrentPage(0);
  };
  const toggleDeleted = () => { setFilters((p) => ({ ...p, showDeleted: !p.showDeleted, showEdited: false })); setCurrentPage(0); };
  const toggleEdited  = () => { setFilters((p) => ({ ...p, showEdited: !p.showEdited, showDeleted: false })); setCurrentPage(0); };
  const handleSort    = (field: string) => {
    setFilters((p) => ({
      ...p, sortBy: field,
      sortDir: p.sortBy === field && p.sortDir === "desc" ? "asc" : "desc",
    }));
    setCurrentPage(0);
  };

  // ── modal helpers ─────────────────────────────────────────────────────────────
  const openCreate = () => {
    const saved = localStorage.getItem(ADMIN_CREATE_DRAFT_KEY) || "";
    setCreateMatchId(""); setCreateContent(saved);
    setCreatePreview(false);
    setCreateDraftRestored(!!saved.trim());
    setModalError(null); setModalType("create");
  };
  const openView  = (e: AdminEntryDto) => { setSelectedEntry(e); setModalError(null); setModalType("view"); };
  const openEdit  = (e: AdminEntryDto) => {
    const saved = localStorage.getItem(adminEditDraftKey(e.id)) || "";
    // Prefer saved draft over original content so partial edits are not lost
    setSelectedEntry(e); setEditContent(saved || e.content); setEditReason("");
    setEditPreview(false);
    setEditDraftRestored(!!saved.trim() && saved !== e.content);
    setModalError(null); setModalType("edit");
  };
  const openDelete = (e: AdminEntryDto) => { setSelectedEntry(e); setDeleteReason(""); setModalError(null); setModalType("delete"); };
  const openHard  = (e: AdminEntryDto) => { setSelectedEntry(e); setModalError(null); setModalType("hardDelete"); };
  const closeModal = () => {
    setModalType(null); setSelectedEntry(null); setModalError(null);
    setCreatePreview(false); setEditPreview(false);
    setCreateDraftRestored(false); setEditDraftRestored(false);
  };

  // ── write operations ──────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!createMatchId || !createContent.trim()) return;
    try {
      await createAdminEntry({ matchId: Number(createMatchId), content: createContent });
      setSuccessMessage(t("admin_entry_created", "Tanım oluşturuldu."));
      localStorage.removeItem(ADMIN_CREATE_DRAFT_KEY);
      closeModal(); loadEntries();
    } catch (err: any) {
      setModalError(err.message || t("admin_entry_create_error"));
    }
  };

  const handleEdit = async () => {
    if (!selectedEntry) return;
    try {
      await updateAdminEntry(selectedEntry.id, { content: editContent, reason: editReason.trim() || undefined });
      setSuccessMessage(t("admin_entry_updated", "Tanım güncellendi."));
      localStorage.removeItem(adminEditDraftKey(selectedEntry.id));
      closeModal(); loadEntries();
    } catch (err: any) {
      setModalError(err.message || t("admin_entry_update_error"));
    }
  };

  const handleDelete = async () => {
    if (!selectedEntry) return;
    try {
      await deleteAdminEntry(selectedEntry.id, deleteReason ? { reason: deleteReason } : {});
      setSuccessMessage(t("admin_entry_deleted", "Tanım silindi."));
      // Close modal first, then reload so the refreshed list reflects the deletion
      closeModal();
      await loadEntries();
    } catch (err: any) {
      setModalError(err.message || t("admin_entry_delete_error"));
    }
  };

  const handleRestore = async (entry: AdminEntryDto) => {
    try {
      await restoreAdminEntry(entry.id);
      setSuccessMessage(t("admin_entry_restored", "Tanım geri yüklendi."));
      loadEntries();
    } catch (err: any) {
      setSuccessMessage(""); // clear; no modal open so show inline
    }
  };

  const handleHardDelete = async () => {
    if (!selectedEntry) return;
    try {
      await hardDeleteAdminEntry(selectedEntry.id);
      setSuccessMessage(t("admin_entry_hard_deleted", "Tanım kalıcı olarak silindi."));
      closeModal(); loadEntries();
    } catch (err: any) {
      setModalError(err.message || t("admin_entry_hard_delete_error"));
    }
  };

  // ── render ────────────────────────────────────────────────────────────────────
  const hasActiveFilters = !!filters.search || !!filters.authorUsername || !!filters.targetTitle || !!filters.targetType || filters.showDeleted || filters.showEdited;

  return (
    <div className="admin-entries">

      <AdminSuccessBanner message={successMessage} onDismiss={clearSuccess} />

      {/* Toolbar — same structure as AdminMatches */}
      <div className="users-toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder={t("admin_entry_search_placeholder", "İçerik veya yazar ara…")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
          />
          {(searchInput !== "" || !!filters.search) && (
            <InputClearButton
              variant="inline"
              className="search-box-clear"
              onClear={clearSearch}
            />
          )}
          <button className="search-btn" onClick={applySearch}><FaSearch /></button>
        </div>

        <button
          className={`filter-toggle ${showFilters ? "active" : ""}`}
          onClick={() => setShowFilters((v) => !v)}
        >
          <FaFilter /> {t("admin_filters", "Filtreler")}
        </button>

        <button
          className={`filter-toggle ${filters.showDeleted ? "active deleted-toggle" : ""}`}
          onClick={toggleDeleted}
        >
          <FaTrash />{" "}
          {filters.showDeleted
            ? t("admin_entry_hide_deleted", "Silinenler Gizle")
            : t("admin_entry_show_deleted", "Silinenler")}
        </button>

        <button
          className={`filter-toggle ${filters.showEdited ? "active edited-toggle" : ""}`}
          onClick={toggleEdited}
        >
          <FaEdit />{" "}
          {filters.showEdited
            ? t("admin_entry_hide_edited", "Düzenlenenler Gizle")
            : t("admin_entry_show_edited", "Düzenlenenler")}
        </button>

        {hasActiveFilters && (
          <button className="clear-filters" onClick={clearFilters}>
            <FaTimes /> {t("admin_clear_filters", "Temizle")}
          </button>
        )}

        <span className="entries-count-label">
          {t("admin_total", "Toplam")}: <strong>{totalItems}</strong>
        </span>

        {/* New Entry button — same position as admin_match_create */}
        <button className="create-entry-btn" onClick={openCreate}>
          <FaPlus /> {t("admin_entry_create", "Yeni Tanım")}
        </button>
      </div>

      {/* Extended filter panel */}
      {showFilters && (
        <div className="admin-filter-panel">
          <div className="admin-filter-group">
            <label>{t("admin_filter_author_name", "Yazar Adı")}</label>
            <input
              type="text" placeholder={t("admin_filter_author_name_placeholder", "kullanıcı adı")}
              value={filters.authorUsername ?? ""}
              onChange={(e) => { setFilters((p) => ({ ...p, authorUsername: e.target.value })); setCurrentPage(0); }}
            />
          </div>
          <div className="admin-filter-group">
            <label>{t("admin_filter_title", "Başlık")}</label>
            <input
              type="text" placeholder={t("admin_filter_title_placeholder", "başlık ara…")}
              value={filters.targetTitle ?? ""}
              onChange={(e) => { setFilters((p) => ({ ...p, targetTitle: e.target.value })); setCurrentPage(0); }}
            />
          </div>
          <div className="admin-filter-group">
            <label>{t("admin_filter_title_type", "Başlık Tipi")}</label>
            <select
              value={filters.targetType ?? ""}
              onChange={(e) => {
                setFilters((p) => ({ ...p, targetType: e.target.value as EntryTargetTypeEnum | "" }));
                setCurrentPage(0);
              }}
            >
              <option value="">{t("admin_filter_title_type_all", "Tümü")}</option>
              {Object.values(EntryTargetTypeEnum).map((type) => (
                <option key={type} value={type}>
                  {t(`entry_target_type_${type.toLowerCase()}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-filter-group">
            <label>{t("admin_filter_date_from", "Başlangıç Tarihi")}</label>
            <input
              type="date" value={filters.startDate ?? ""}
              onChange={(e) => { setFilters((p) => ({ ...p, startDate: e.target.value })); setCurrentPage(0); }}
            />
          </div>
          <div className="admin-filter-group">
            <label>{t("admin_filter_date_to", "Bitiş Tarihi")}</label>
            <input
              type="date" value={filters.endDate ?? ""}
              onChange={(e) => { setFilters((p) => ({ ...p, endDate: e.target.value })); setCurrentPage(0); }}
            />
          </div>
        </div>
      )}

      {/* Deleted bin notice */}
      {filters.showDeleted && (
        <div className="admin-entries-bin-notice">
          <FaExclamationTriangle />
          {t("admin_entry_bin_notice", "Moderasyon sepeti — silinmiş tanımlar görüntüleniyor.")}
        </div>
      )}

      {filters.showEdited && (
        <div className="admin-entries-bin-notice admin-entries-edited-notice">
          <FaEdit />
          {t("admin_entry_edited_notice", "Düzenleme kaydı — admin/mod tarafından düzenlenmiş tanımlar görüntüleniyor.")}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="entries-loading">
          <div className="entries-spinner" />
          <span>{t("loading", "Yükleniyor…")}</span>
        </div>
      ) : entries.length === 0 ? (
        <div className="entries-empty">{t("admin_entry_no_results", "Tanım bulunamadı.")}</div>
      ) : (
        <div className="entries-table-container">
          <table className="entries-table">
            {/* Explicit column widths keep cells from drifting */}
            <colgroup>
              <col style={{ width: "60px" }}  />  {/* # */}
              <col style={{ width: "130px" }} />  {/* Author */}
              <col />                             {/* Content — flex, gets most space */}
              <col style={{ width: "160px" }} />  {/* Match */}
              <col style={{ width: "120px" }} />  {/* Date */}
              <col style={{ width: "100px" }} />  {/* Votes */}
              {filters.showDeleted && <col style={{ width: "140px" }} />}
              <col style={{ width: "130px" }} />  {/* Edited by */}
              <col style={{ width: "160px" }} />  {/* Actions — wider to fit reason button */}
            </colgroup>
            <thead>
              <tr>
                <th className="sortable-col" onClick={() => handleSort("id")}>
                  # <SortIcon field="id" sortBy={filters.sortBy} sortDir={filters.sortDir} />
                </th>
                <th>{t("admin_entry_col_author", "Yazar")}</th>
                <th>{t("admin_entry_col_content", "İçerik")}</th>
                <th>{t("admin_entry_col_match", "Bağlam")}</th>
                <th className="sortable-col" onClick={() => handleSort("createdAt")}>
                  {t("admin_entry_col_date", "Tarih")}{" "}
                  <SortIcon field="createdAt" sortBy={filters.sortBy} sortDir={filters.sortDir} />
                </th>
                {/* Votes is sortable — backend handles plusCount sort in memory */}
                <th className="sortable-col" onClick={() => handleSort("plusCount")}>
                  {t("admin_entry_col_votes", "Oylar")}{" "}
                  <SortIcon field="plusCount" sortBy={filters.sortBy} sortDir={filters.sortDir} />
                </th>
                {filters.showDeleted && (
                  <th>{t("admin_entry_col_deleted_by", "Silen")}</th>
                )}
                <th>{t("admin_entry_col_edited_by", "Düzenleyen")}</th>
                <th>{t("admin_col_actions", "İşlemler")}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                // Pick the relevant moderation reason for this entry's context.
                // In deleted view prefer deleteReason; otherwise prefer editReason.
                const reasonText = filters.showDeleted
                  ? (entry.deleteReason || entry.editReason || null)
                  : (entry.editReason || entry.deleteReason || null);

                return (
                  <tr key={entry.id} className={entry.deletedAt ? "row-deleted" : ""}>

                    {/* ID — link to match/person/club/team page depending on entry type */}
                    <td>
                      {entry.match ? (
                        <a
                          className="cell-id entry-id-link"
                          href={`${generateMatchUrlFromEntry(
                            entry.match.homeTeamName,
                            entry.match.awayTeamName,
                            new Date(entry.match.matchDate),
                            entry.match.id
                          )}#entry-${entry.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Tanımı maç sayfasında görüntüle"
                        >
                          #{entry.id}
                        </a>
                      ) : entry.person ? (
                        <a
                          className="cell-id entry-id-link"
                          href={`/kisi/${entry.person.slug}#entry-${entry.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Tanımı kişi sayfasında görüntüle"
                        >
                          #{entry.id}
                        </a>
                      ) : entry.club ? (
                        <a
                          className="cell-id entry-id-link"
                          href={`/kulup/${entry.club.slug}#entry-${entry.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Tanımı kulüp sayfasında görüntüle"
                        >
                          #{entry.id}
                        </a>
                      ) : entry.team ? (
                        <a
                          className="cell-id entry-id-link"
                          href={`/takim/${entry.team.clubSlug}/${entry.team.sportSlug}#entry-${entry.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Tanımı takım sayfasında görüntüle"
                        >
                          #{entry.id}
                        </a>
                      ) : entry.topic ? (
                        <a
                          className="cell-id entry-id-link"
                          href={`/gundem/${entry.topic.slug}#entry-${entry.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Tanımı gündem sayfasında görüntüle"
                        >
                          #{entry.id}
                        </a>
                      ) : (
                        <span className="cell-id">#{entry.id}</span>
                      )}
                    </td>

                    {/* Author */}
                    <td>
                      <span className={`author-name status-${entry.author.role.toLowerCase()}`}>
                        {entry.author.username}
                      </span>
                      <span className="author-role">{t(`status_${entry.author.role.toLowerCase()}`)}</span>
                    </td>

                    {/* Content preview — shown first so it's visible without scrolling */}
                    <td>
                      <span className="content-preview" title={entry.content}>
                        {truncateText(entry.content)}
                      </span>
                      {/* An entry counts as edited only when editedBy is set.
                          Timestamp comparison (editedAt !== createdAt) was unreliable:
                          edited_at was bumped by soft-deletes, restores and bulk
                          UPDATEs via the DB-level ON UPDATE CURRENT_TIMESTAMP clause. */}
                      {entry.editedBy !== null && (
                        <span className="content-edited">
                          {t("admin_entry_edited", "düzenlendi")}
                        </span>
                      )}
                    </td>

                    {/* Match / Person / Club / Team / Topic context */}
                    <td>
                      {entry.match ? (
                        <a
                          className="admin-entry-person-link"
                          href={generateMatchUrlFromEntry(
                            entry.match.homeTeamName,
                            entry.match.awayTeamName,
                            new Date(entry.match.matchDate),
                            entry.match.id
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <span className="match-teams">
                            {entry.match.homeTeamName} - {entry.match.awayTeamName}
                          </span>
                          <span className="match-date">{entry.match.matchDate}</span>
                        </a>
                      ) : entry.person ? (
                        <a
                          className="admin-entry-person-link"
                          href={`/kisi/${entry.person.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {entry.person.displayName}
                        </a>
                      ) : entry.club ? (
                        <a
                          className="admin-entry-person-link"
                          href={`/kulup/${entry.club.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {entry.club.shortName}{" "}
                          <span style={{ opacity: 0.6, fontSize: "0.8em" }}>(kulüp)</span>
                        </a>
                      ) : entry.team ? (
                        <a
                          className="admin-entry-person-link"
                          href={`/takim/${entry.team.clubSlug}/${entry.team.sportSlug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {entry.team.displayName}
                          {entry.team.teamCode && (
                            <span style={{ marginLeft: 4, fontWeight: 700, fontSize: "0.8em", opacity: 0.85 }}>
                              {entry.team.teamCode.toUpperCase()}
                            </span>
                          )}{" "}
                          <span style={{ opacity: 0.6, fontSize: "0.8em" }}>(takım)</span>
                        </a>
                      ) : entry.topic ? (
                        <a
                          className="admin-entry-person-link"
                          href={`/gundem/${entry.topic.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {entry.topic.title}{" "}
                          <span style={{ opacity: 0.6, fontSize: "0.8em" }}>(gündem)</span>
                        </a>
                      ) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                    </td>

                    {/* Date */}
                    <td><span className="cell-date">{formatAdminDate(entry.createdAt)}</span></td>

                    {/* Votes */}
                    <td>
                      <div className="vote-chips">
                        <span className="vote-chip up" title={t("admin_entry_votes_up", "Beğeni")}>
                          <FaChevronUp /> {entry.plusCount}
                        </span>
                        <span className="vote-chip down" title={t("admin_entry_votes_down", "Beğenmeme")}>
                          <FaChevronDown /> {entry.minusCount}
                        </span>
                        <span className="vote-chip fav" title={t("admin_entry_votes_fav", "Favori")}>
                          <FaStar /> {entry.favCount}
                        </span>
                      </div>
                    </td>

                    {/* Deleted by — only in bin view, reason is now shown in the actions column */}
                    {filters.showDeleted && (
                      <td>
                        {entry.deletedBy ? (
                          <>
                            <span className="deleted-by-name">{entry.deletedBy.username}</span>
                            <span className="deleted-at">{formatAdminDate(entry.deletedAt)}</span>
                          </>
                        ) : "—"}
                      </td>
                    )}

                    {/* Edited by — reason is now shown in the actions column */}
                    <td>
                      {entry.editedBy ? (
                        <>
                          <span className="deleted-by-name">{entry.editedBy.username}</span>
                          <span className="deleted-at">{formatAdminDate(entry.editedAt)}</span>
                        </>
                      ) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                    </td>

                    {/* Actions — reason button is always the rightmost button */}
                    <td>
                      <div className="entry-actions">
                        <button className="action-btn view"
                          onClick={() => openView(entry)}
                          title={t("admin_action_view", "Görüntüle")}>
                          <FaEye />
                        </button>

                        {!entry.deletedAt && (
                          <button className="action-btn edit"
                            onClick={() => openEdit(entry)}
                            title={t("admin_action_edit", "Düzenle")}>
                            <FaEdit />
                          </button>
                        )}

                        {!entry.deletedAt && (
                          <button className="action-btn danger"
                            onClick={() => openDelete(entry)}
                            title={t("admin_action_delete", "Sil")}>
                            <FaTrash />
                          </button>
                        )}

                        {entry.deletedAt && isAdmin && (
                          <button className="action-btn success"
                            onClick={() => handleRestore(entry)}
                            title={t("admin_action_restore", "Geri Yükle")}>
                            <FaTrashRestore />
                          </button>
                        )}

                        {isAdmin && (
                          <button className="action-btn warning"
                            onClick={() => openHard(entry)}
                            title={t("admin_action_hard_delete", "Kalıcı Sil")}>
                            <FaExclamationTriangle />
                          </button>
                        )}

                        {/* Reason button — rightmost, only shown when a moderation note exists */}
                        {reasonText && (
                          <button
                            className="action-btn reason"
                            onClick={() => setReasonModalText(reasonText)}
                            title={t("admin_entry_view_reason", "Moderatör notunu görüntüle")}
                          >
                            <FaCommentAlt />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination — 1-based component, 0-based state */}
      {!loading && totalPages > 1 && (
        <Pagination
          currentPage={currentPage + 1}
          totalPages={totalPages}
          totalItems={totalItems}
          paginate={(pageNumber: number) => setCurrentPage(pageNumber - 1)}
        />
      )}

      {/* ==================== MODALS ==================== */}

      {reasonModalText && (
        <ReasonModal text={reasonModalText} onClose={() => setReasonModalText(null)} />
      )}

      {modalType === "create" && (
        <CreateEntryModal
          matchId={createMatchId}
          setMatchId={setCreateMatchId}
          content={createContent}
          setContent={setCreateContent}
          preview={createPreview}
          setPreview={setCreatePreview}
          draftRestored={createDraftRestored}
          onDiscardDraft={() => {
            setCreateContent("");
            setCreateDraftRestored(false);
            localStorage.removeItem(ADMIN_CREATE_DRAFT_KEY);
          }}
          textareaRef={createTextareaRef}
          error={modalError}
          onClose={closeModal}
          onSubmit={handleCreate}
        />
      )}

      {modalType === "view" && selectedEntry && (
        <ViewEntryModal entry={selectedEntry} onClose={closeModal} />
      )}

      {modalType === "edit" && selectedEntry && (
        <EditEntryModal
          entry={selectedEntry}
          content={editContent}
          setContent={setEditContent}
          reason={editReason}
          setReason={setEditReason}
          preview={editPreview}
          setPreview={setEditPreview}
          draftRestored={editDraftRestored}
          onDiscardDraft={() => {
            setEditContent(selectedEntry.content);
            setEditDraftRestored(false);
            localStorage.removeItem(adminEditDraftKey(selectedEntry.id));
          }}
          textareaRef={editTextareaRef}
          error={modalError}
          onClose={closeModal}
          onSubmit={handleEdit}
        />
      )}

      {modalType === "delete" && selectedEntry && (
        <DeleteEntryModal
          entry={selectedEntry}
          reason={deleteReason}
          setReason={setDeleteReason}
          error={modalError}
          onClose={closeModal}
          onSubmit={handleDelete}
        />
      )}

      {modalType === "hardDelete" && selectedEntry && (
        <HardDeleteEntryModal
          entry={selectedEntry}
          error={modalError}
          onClose={closeModal}
          onSubmit={handleHardDelete}
        />
      )}
    </div>
  );
};